"use client"

import { useMemo } from "react"
import dynamic from "next/dynamic"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { usePathname } from "next/navigation"
import { publicPathname } from "@/lib/public-pathname"
import { isCoverageCheckerPath } from "@/lib/insurance/coverage-checker-path"
import { TopBanner } from "@/components/ui/top-banner"
import { Navbar } from "@/components/ui/navbar"
import { Footer } from "@/components/ui/footer"
import { useLocalizedNavigation } from "@/lib/use-localized-navigation"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { useCountry } from "@/components/layout/country-provider"
import { useNavHiddenPaths } from "@/components/layout/nav-hidden-paths-provider"
import { SearchProvider } from "@/components/search/search-provider"
import {
  bookingUrlForCountry,
  membershipBookingUrl,
  membershipBookingAppUrl,
} from "@/lib/booking"
import { FooterLocalePicker } from "@/components/ui/navbar/locale-picker"
import { LogoBrandMenu } from "@/components/ui/logo-brand-menu"
import { CrossBorderNotice } from "@/components/ui/cross-border-notice"
import { isWaitlistOnly, MARKET_SUPPORT } from "@/i18n/config"
import {
  showsTopBanner,
  topBannerKeys,
  topBannerLinkPage,
} from "@/lib/top-banner"
import { isAllowedInCountry } from "@/lib/country-gates"

const NAV_LOGO_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="113"
    height="32"
    fill="none"
    viewBox="0 0 124 35"
    className="h-6 w-auto md:h-[35px]"
    role="img"
    aria-label="Ahead Health"
  >
    <path
      fill="currentColor"
      d="M14.34 3.216 2.4 32.158h2.945l3.357-8.27h14.303l3.357 8.27h3.11L17.533 3.216H14.34Zm-4.642 18.15 6.176-15.174 6.138 15.173H9.698Zm32.395-11.123c-3.4 0-5.887 1.738-7.089 4.052V2.386H32.27v29.771h2.735V19.796c0-4.714 3.026-7.153 6.344-7.153 3.318 0 5.722 2.108 5.722 6.326V32.16h2.736V18.64c0-5.707-3.151-8.394-7.71-8.394l-.003-.002Zm21.541.002c-5.264 0-10.197 4.052-10.197 11.04 0 6.99 4.56 11.33 10.406 11.33 4.52 0 8.5-2.314 9.743-6.574h-2.735c-1.202 2.77-3.69 4.219-6.924 4.219-4.311 0-7.504-3.432-7.71-8.23h17.495v-.745c0-7.236-4.727-11.04-10.075-11.04h-.003Zm-7.42 9.55c.332-4.508 3.608-7.236 7.379-7.236 3.77 0 7.088 2.728 7.297 7.236H56.214Zm62.651-17.409v12.487c-1.16-2.647-4.063-4.63-7.752-4.63-5.265 0-9.908 4.135-9.908 11.04 0 6.906 4.518 11.33 9.908 11.33 3.689 0 6.634-2.274 7.752-5.044v4.588h2.735V2.387h-2.735Zm-7.381 27.87c-4.228 0-7.462-3.433-7.462-8.973 0-5.54 3.234-8.682 7.462-8.682 3.773 0 7.42 2.77 7.42 8.682 0 5.913-3.689 8.972-7.42 8.972ZM92.677 10.477H77.15v2.737l15.418-.38a2.445 2.445 0 0 1 2.509 2.49l-.335 16.834h2.694V15.225a4.755 4.755 0 0 0-4.76-4.748h.002Z"
    />
    <path
      fill="currentColor"
      d="M89.47 16.834 76.677 30.626l1.946 1.99L91.49 18.695a1.366 1.366 0 0 0-.094-1.949 1.375 1.375 0 0 0-1.925.089Z"
    />
  </svg>
)

const FOOTER_LOGO_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="124"
    height="35"
    fill="none"
    viewBox="0 0 124 35"
    className="h-[35px] w-[124px]"
    role="img"
    aria-label="Ahead Health"
  >
    <path
      fill="currentColor"
      d="M14.34 3.216 2.4 32.158h2.945l3.357-8.27h14.303l3.357 8.27h3.11L17.533 3.216H14.34Zm-4.642 18.15 6.176-15.174 6.138 15.173H9.698Zm32.395-11.123c-3.4 0-5.887 1.738-7.089 4.052V2.386H32.27v29.771h2.735V19.796c0-4.714 3.026-7.153 6.344-7.153 3.318 0 5.722 2.108 5.722 6.326V32.16h2.736V18.64c0-5.707-3.151-8.394-7.71-8.394l-.003-.002Zm21.541.002c-5.264 0-10.197 4.052-10.197 11.04 0 6.99 4.56 11.33 10.406 11.33 4.52 0 8.5-2.314 9.743-6.574h-2.735c-1.202 2.77-3.69 4.219-6.924 4.219-4.311 0-7.504-3.432-7.71-8.23h17.495v-.745c0-7.236-4.727-11.04-10.075-11.04h-.003Zm-7.42 9.55c.332-4.508 3.608-7.236 7.379-7.236 3.77 0 7.088 2.728 7.297 7.236H56.214Zm62.651-17.409v12.487c-1.16-2.647-4.063-4.63-7.752-4.63-5.265 0-9.908 4.135-9.908 11.04 0 6.906 4.518 11.33 9.908 11.33 3.689 0 6.634-2.274 7.752-5.044v4.588h2.735V2.387h-2.735Zm-7.381 27.87c-4.228 0-7.462-3.433-7.462-8.973 0-5.54 3.234-8.682 7.462-8.682 3.773 0 7.42 2.77 7.42 8.682 0 5.913-3.689 8.972-7.42 8.972ZM92.677 10.477H77.15v2.737l15.418-.38a2.445 2.445 0 0 1 2.509 2.49l-.335 16.834h2.694V15.225a4.755 4.755 0 0 0-4.76-4.748h.002Z"
    />
    <path
      fill="currentColor"
      d="M89.47 16.834 76.677 30.626l1.946 1.99L91.49 18.695a1.366 1.366 0 0 0-.094-1.949 1.375 1.375 0 0 0-1.925.089Z"
    />
  </svg>
)

// Dynamic imports — lazy-loaded on demand, not bundled into the global shell chunk.
const WaitlistModal = dynamic(
  () =>
    import("@/components/ui/waitlist").then((m) => ({
      default: m.WaitlistModal,
    })),
  { ssr: false, loading: () => null }
)
const SearchModal = dynamic(
  () =>
    import("@/components/search/search-modal").then((m) => ({
      default: m.SearchModal,
    })),
  { ssr: false, loading: () => null }
)
const SearchAiDrawer = dynamic(
  () =>
    import("@/components/search/search-ai-drawer").then((m) => ({
      default: m.SearchAiDrawer,
    })),
  { ssr: false, loading: () => null }
)
const LocaleSuggestion = dynamic(
  () =>
    import("@/components/ui/locale-suggestion").then((m) => ({
      default: m.LocaleSuggestion,
    })),
  { ssr: false, loading: () => null }
)
const ExitIntentProvider = dynamic(
  () =>
    import("@/components/exit-intent/exit-intent-provider").then((m) => ({
      default: m.ExitIntentProvider,
    })),
  { ssr: false, loading: () => null }
)

// AHD-6934: agent runtime verification — safe to revert.
export function PageShell({
  children,
  transparentNav = false,
}: {
  children: React.ReactNode
  /**
   * Render the navbar in hero-overlay mode (transparent at scroll-top,
   * glass when scrolled). Passed in by the route group's layout so SSR
   * already produces the correct state — no `usePathname()` branching
   * inside PageShell, which would otherwise flicker on hydration.
   */
  transparentNav?: boolean
}) {
  const { code: country } = useCountry()
  // Bare `/tests/<slug>` paths hidden from the core nav (mega menu + mobile
  // menu) for this country — the CMS `services.hideFromNav` per-country flag.
  //
  // Read from context, NOT a prop: it comes from an `await getServicesConfig()`
  // that has to happen in `[locale]/layout.tsx` rather than in the route-group
  // layout that renders this component. Awaiting one level down suspends that
  // file's `<Suspense fallback={null}>` and drops the whole visible page out of
  // the static shell (AHD-9096) — see `nav-hidden-paths-provider.tsx`.
  const navHiddenPaths = useNavHiddenPaths()
  // Membership-first positioning (CH + AT). Drives the navbar CTA swap below AND
  // the membership nav treatment (mega-menu membership lead, no standalone
  // Membership item) inside useLocalizedNavigation. Country-derived, so it's
  // known at render on both server and client — no flag read, no flash. NL + DE
  // keep the testing-first nav. See lib/country-gates.ts.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  const { navItems, footerColumns, footerLabels } = useLocalizedNavigation({
    membershipFirst: isMembershipFirst,
    navHiddenPaths,
  })
  const t = useTranslations("nav")
  const ut = useTranslations("ui")
  const { openWaitlist } = useWaitlist()
  const locale = useLocale()
  // Membership and test-detail pages sit inside `(default-nav)`, so they can't
  // signal a hero-overlay navbar via `transparentNav` from a layout the way
  // `(transparent-nav)` routes do — the path is what tells them apart.
  //
  // `publicPathname()` is load-bearing, not defensive: `usePathname()` gives
  // the middleware-REWRITTEN path during SSR (`/ch/en/tests/v/{code}/{slug}`)
  // and the PUBLIC one on the client (`/ch/en/tests/{slug}`). The exact-length
  // check below reads false on the server and true on the client for the same
  // page, which rendered the opaque navbar into the HTML and the transparent
  // one on hydration — React #418 on every test-detail page plus a visible
  // navbar flash (MARKETING-SITE-N). Normalise first so both sides agree.
  const pathname = publicPathname(usePathname())
  const pathSegments = pathname.split("/").filter(Boolean)
  const isMembership = pathSegments.includes("membership")
  // Test detail pages (`/{country}/{locale}/tests/{slug}`) share membership's
  // hero shape on mobile: full-bleed dark image, transparent navbar overlaying
  // it. Excludes the listing (`/tests`), whose variant form normalises back to
  // 3 segments.
  const isTestsDetail =
    pathSegments.length === 4 &&
    pathSegments[2] === "tests" &&
    pathSegments[3] !== "v"
  const useHeroOverlayNav = isMembership || isTestsDetail
  const isCoverageChecker = isCoverageCheckerPath(pathname)
  // The top banner shows on every page — no page-specific exceptions. (Test
  // detail pages previously dropped it so the dark hero sat flush against the
  // viewport top on mobile; that exception is gone — the promo runs site-wide.)
  // Except Germany, which runs without one (see lib/top-banner.ts). The navbar
  // is told too, so its transparent variant stops reserving the banner's 36px.
  const showBanner = showsTopBanner(country)
  // Markets in pre-launch / waitlist-only mode (NL today, DE today). Drives
  // the CTA → waitlist swap, the banner link target, and hiding the footer's
  // contact channels. Flips automatically when the country's
  // <KEY>_LAUNCHED env flag turns on — see `isWaitlistOnly` in i18n/config.
  const isWaitlist = isWaitlistOnly(country)
  // The footer's phone and WhatsApp, per market: the Swiss line for CH/DE/AT,
  // the Dutch one for NL. Both used to be hardcoded Swiss and hidden only by
  // `isWaitlist`, so the day NL left waitlist mode its footer would have
  // offered Dutch customers a +41 number.
  const support = MARKET_SUPPORT[country]
  // In membership markets the primary navbar CTA becomes "Become a member"
  // site-wide, mirroring the homepage.
  const wantMembershipCta = isMembership || isMembershipFirst
  // NL + AT pre-launch: the top banner announces the launch city and its CTA
  // opens the waitlist modal instead of linking anywhere.
  const bannerOpensWaitlist =
    isWaitlist && (country === "nl" || country === "at")
  // Which banner copy this market shows, and where its link goes — both keyed
  // off the rollout status, so they turn over with the env var rather than
  // needing a copy PR timed to the flip. See lib/top-banner.ts.
  const bannerKeys = topBannerKeys(country)
  const bannerLinkPage = topBannerLinkPage(country)
  // The banner's non-waitlist target is the membership page — but only where
  // that page exists. NL sells no membership at all (`page:membership` is CH
  // + DE, AHD-9361), so a launched NL must not link there: TopBanner renders
  // the href twice (mobile + desktop variants both sit in the DOM), and one
  // ungated linkHref was two dead /nl/*/membership anchors on every page —
  // including the tool pages, whose own panel correctly swaps to the check-up
  // CTA. Fall back to /locations, the listing that renders in every market.
  const hasMembershipPage = isAllowedInCountry("page:membership", country)

  const commonNavProps = useMemo(
    () => ({
      logo: <LogoBrandMenu>{NAV_LOGO_SVG}</LogoBrandMenu>,
      items: navItems,
      // `wantMembershipCta` folds the membership markets into the existing
      // /membership-page CTA swap.
      //
      // The membership-first markets (CH + AT,
      // `feature:membership-first-positioning`) lead with "Book check-up" — the
      // concluded winner of the main-CTA copy experiment (see lib/experiments.ts),
      // which replaced the "Become a member" ask. A non-membership-first market
      // reaching this branch via a /membership path keeps "Become a member".
      // Waitlist market: the navbar CTA opens the waitlist modal, so the
      // label reads "Join waitlist" in every locale — no per-locale drift.
      ctaLabel: isWaitlist
        ? ut("joinWaitlist")
        : wantMembershipCta
          ? isMembershipFirst
            ? ut("mainCtaStartCheckup")
            : t("becomeAMember")
          : t("bookCheckup"),
      ctaHref: isWaitlist
        ? undefined
        : // Membership markets route "Become a member" through the booking app's
          // membership flow (booking.aheadhealth.com `step=cadence`);
          // testing-first markets' /membership pages keep the standalone app.
          isMembershipFirst
          ? membershipBookingAppUrl(locale)
          : wantMembershipCta
            ? membershipBookingUrl(locale)
            : // Check-up (blood) CTA: DE books via the staging app, other
              // markets delegate to production inside bookingUrlForCountry.
              bookingUrlForCountry(country, locale),
      onCtaClick: isWaitlist ? () => openWaitlist() : undefined,
      navHiddenPaths,
      bannerVisible: showBanner,
    }),
    [
      navItems,
      wantMembershipCta,
      isMembershipFirst,
      isWaitlist,
      country,
      locale,
      openWaitlist,
      navHiddenPaths,
      showBanner,
      t,
      ut,
    ]
  )

  return (
    <SearchProvider>
      {showBanner && (
        <TopBanner
          message={ut(bannerKeys.message)}
          messageShort={ut(bannerKeys.messageShort)}
          linkText={ut(bannerKeys.link)}
          {...(bannerOpensWaitlist
            ? { onLinkClick: () => openWaitlist() }
            : {
                linkHref: `/${country}/${locale}/${isWaitlist || !hasMembershipPage ? "locations" : bannerLinkPage}`,
              })}
        />
      )}
      {useHeroOverlayNav ? (
        // Mobile: transparent (hero peeks through). Desktop: solid.
        // `contents` wrappers + md:hidden/max-md:hidden keep this pure CSS so
        // both variants are rendered at SSR with no matchMedia flicker.
        <>
          <div className="contents md:hidden">
            <Navbar {...commonNavProps} transparent />
          </div>
          <div className="contents max-md:hidden">
            <Navbar {...commonNavProps} transparent={false} />
          </div>
        </>
      ) : (
        <Navbar {...commonNavProps} transparent={transparentNav} />
      )}
      <main id="main" className="bg-background min-h-screen overflow-x-clip">
        {children}
      </main>
      <Footer
        logo={<LogoBrandMenu>{FOOTER_LOGO_SVG}</LogoBrandMenu>}
        tagline={footerLabels.tagline}
        phone={isWaitlist ? undefined : support.phone}
        email="get@aheadhealth.com"
        whatsappHref={
          isWaitlist ? undefined : `https://wa.me/${support.whatsappDigits}`
        }
        whatsappLabel={isWaitlist ? undefined : footerLabels.whatsapp}
        socialLinks={[
          {
            platform: "linkedin",
            href: "https://www.linkedin.com/company/ahead-health/",
          },
          {
            platform: "instagram",
            href: "https://www.instagram.com/ahead.health",
          },
          { platform: "facebook", href: "https://facebook.com/aheadhealth" },
        ]}
        columns={footerColumns}
        copyright={footerLabels.copyright}
        legalLinks={footerLabels.legalLinks}
        cookieSettingsLabel={footerLabels.cookieSettings}
        locale={locale}
        localePicker={
          // The navbar carries the picker on desktop (`hidden md:block`).
          // Keep the footer picker visible on mobile so there's always one
          // locale picker reachable regardless of viewport.
          <div className="md:hidden">
            <FooterLocalePicker />
          </div>
        }
        crossBorderNotice={
          country === "de" ? (
            <CrossBorderNotice variant="footer" className="mt-2" />
          ) : undefined
        }
      />
      <WaitlistModal />
      <LocaleSuggestion />
      {/* Not on the coverage checker: it interrupted people mid-form and never
          converted there — see `isCoverageCheckerPath`. */}
      {!isCoverageChecker && <ExitIntentProvider />}
      <SearchModal />
      <SearchAiDrawer />
    </SearchProvider>
  )
}
